import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function AchievementForm({ open, onClose, onSuccess, isTrap = false }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    par_score: '',
    image_url: '',
    is_trap: isTrap,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setError(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Auto-upload immediately
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        timeout: 60000, // 60 second timeout
      });

      // Update form data without resetting other fields
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      setUploading(false);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError('Failed to upload image. Please try again.');
      setUploading(false);
      setImageFile(null);
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.par_score) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.image_url) {
      setError('Please upload an image');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await axios.post(
        `${API_URL}/api/achievements`,
        {
          name: formData.name,
          description: formData.description,
          par_score: parseInt(formData.par_score),
          image_url: formData.image_url,
          is_trap: formData.is_trap || isTrap,
        }
      );

      // Reset form
      setFormData({
        name: '',
        description: '',
        par_score: '',
        image_url: '',
        is_trap: isTrap,
      });
      setImageFile(null);
      setImagePreview(null);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating achievement:', error);
      setError(error.response?.data?.detail || 'Failed to create achievement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      par_score: '',
      image_url: '',
      is_trap: isTrap,
    });
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{isTrap ? 'Add Trap' : 'Add Handicap'}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Achievement Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            margin="normal"
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={3}
            margin="normal"
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Par Score"
            name="par_score"
            type="number"
            value={formData.par_score}
            onChange={handleChange}
            required
            margin="normal"
            variant="outlined"
            inputProps={{ min: -999, max: 999 }}
            helperText="The total score required to earn this achievement (can be negative for handicaps)"
          />

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Achievement Image *
            </Typography>
            <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : formData.image_url ? 'Change Image' : 'Select Image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
              {uploading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Uploading...
                  </Typography>
                </Box>
              )}
            </Box>
            {imagePreview && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              </Box>
            )}
            {formData.image_url && !uploading && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Image uploaded successfully!
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !formData.image_url || uploading}
        >
          {saving ? 'Creating...' : 'Create Achievement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AchievementForm;

